import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { DatetimeFormatType, TypeOfDate } from 'app/lib/datetime/types';
import type { ReduxState } from 'app/redux/types';

import Datetime from 'app/design-system/components/Datetime';
import { ImagePreloader } from 'app/design-system/components/ImagePreloader';
import { useTranslation } from 'app/lang';
import type { RecordOfObjectEntity } from 'app/lib/betting/betting-entities';
import { SlotType } from 'app/lib/betting/betting-entities';
import { getSportKey } from 'app/lib/betting/sport-utils';
import { getTeamLogosUrls } from 'app/lib/compose-image-src';
import { BreadcrumbLink } from 'app/modules/Betting';
import { composeMatchCenterPath, getPrefixBySlotType } from 'app/modules/Navigation/utils';

import { createMatchObjectsSelector, isMatchStarted, matches } from '../../selectors/entities';
import { Breadcrumbs, gridSubheaderBreadcrumbSelector } from '../../selectors/grid';
import MainStakes from '../MainStakes';
import MatchDetails from './MatchDetails';

import dictionary from './MatchTitle.dictionary.json';

import styles from './MatchTile.module.scss';

// ---

type StoreProps = {
  isLive: boolean;
  sportId?: number;
  tournamentUnionId?: number;
  tournamentId?: number;
  tournamentStageId?: number;
  slotType?: number;
  breadcrumbs: Breadcrumbs;
  matchName: string;
  matchAdditionalText?: string;
  matchImage?: string;
  startDate?: Date;
  started: boolean;
  secondTeam?: RecordOfObjectEntity;
  firstTeam?: RecordOfObjectEntity;
};

type MatchTileProps = {
  matchId: number;
  image: string | null;
};

const SECOND_LINK_ELEMENT = 1;
const PENULTIMATE_LINK_ELEMENT = 2;
const LINK_OFFSET_TOP = 14;
const TEAM_LOGO_SIZE = 50;

const MatchTile: FC<MatchTileProps & StoreProps> = ({
  isLive,
  sportId,
  tournamentUnionId,
  tournamentId,
  tournamentStageId,
  matchId,
  matchName,
  image,
  // matchAdditionalText,
  slotType,
  startDate,
  started,
  breadcrumbs,
  firstTeam,
  secondTeam,
}) => {
  const sportKey = sportId ? getSportKey(sportId) : 'default';
  const [emptySpace, setEmptySpace] = useState(true);
  const { t } = useTranslation();

  const pathname = slotType && getPrefixBySlotType(slotType);

  const matchIntegrity = (
    sportId
    && tournamentUnionId
    && tournamentId
    && tournamentStageId
    && matchId
  );

  const matchNameAcrossHyphen = matchName.replace(' - ', ' — '); // Прихоти дизайна :)

  const _matchClassName = classNames(
    styles.matchName,
    styles.hideText, {
      [styles.matchWithLink]: matchIntegrity && pathname,
    },
  );

  const linkRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    const offsetTop = linkRef?.current?.offsetTop || 0;

    if (offsetTop > LINK_OFFSET_TOP) {
      setEmptySpace(false);
    }
  }, [linkRef]);

  return (
    <div className={styles.container}>
      {image && (
        <img aria-hidden
             alt="Фоновое изображение"
             className={styles.backgroundImage}
             src={image} />
      )}
      <div className={classNames(styles.breadcrumbs, styles.hideText)}>
        {breadcrumbs.map((breadcrumb, index) => {
          const showSpace =
            (index === SECOND_LINK_ELEMENT && emptySpace)
            || (index !== PENULTIMATE_LINK_ELEMENT);

          return (
            <Fragment key={breadcrumb.type}>
              <BreadcrumbLink ref={index === SECOND_LINK_ELEMENT ? linkRef : null}
                              dataGaEvent="matchOfDay-crumbsClick"
                              breadcrumb={breadcrumb}
                              className={classNames(styles.breadcrumb, {
                                [styles.moveLink]: showSpace,
                              })} />
              {showSpace && ' '}
            </Fragment>
          );
        })}
      </div>

      {(matchIntegrity && pathname) ? (
        <Link className={_matchClassName}
              data-ga-event="matсhofday-openGroup"
              data-ga-sport-match={matchId}
              to={
                composeMatchCenterPath({
                  sportId,
                  tournamentUnionId,
                  tournamentId,
                  tournamentStageId,
                  matchId,
                }, pathname)
              }>
          {matchNameAcrossHyphen}
        </Link>
      ) : (
        <div className={styles.matchName}>{matchNameAcrossHyphen}</div>
      )}

      <div className={styles.matchDetailsWrapper}>
        {isLive && (
          <span className={styles.liveIcon}
                title={t(dictionary.liveTitle)}>
            LIVE
          </span>
        )}
        {started ? (
          <MatchDetails matchId={matchId} />
        ) : (
          <Datetime value={startDate}
                    className={styles.datetime}
                    format={DatetimeFormatType.FRIENDLY}
                    typeOfDate={TypeOfDate.HUMAN_DATE} />
        )}
      </div>

      {/*{matchAdditionalText && (*/}
      {/*  <div className={classNames(styles.additional, styles.hideText)}*/}
      {/*       dangerouslySetInnerHTML={{ __html: matchAdditionalText }} />*/}
      {/*)}*/}

      <div className={styles.mainStakesAndTeamsLogosWrapper}>
        <MainStakes matchId={matchId} />

        <div className={styles.teamsLogosWrapper}>
          <ImagePreloader
            imageSrc={getTeamLogosUrls(firstTeam?.icon)}
            defaultImage={sportKey}
            height={TEAM_LOGO_SIZE}
            width={TEAM_LOGO_SIZE}
            className={styles.teamLogoImg} />
          <ImagePreloader
            imageSrc={getTeamLogosUrls(secondTeam?.icon)}
            defaultImage={sportKey}
            height={TEAM_LOGO_SIZE}
            width={TEAM_LOGO_SIZE}
            className={styles.teamLogoImg} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (_state: ReduxState, { matchId }: MatchTileProps) => {
  const matchObjectsSelector = createMatchObjectsSelector(matchId);

  return (state: ReduxState): StoreProps => {
    const match = matches(state).get(matchId);
    const isLive = match?.slotType === SlotType.LIVE ?? false;
    const matchObjects = matchObjectsSelector(state);

    const props: StoreProps = {
      isLive,
      matchName: '',
      started: isLive && isMatchStarted(state, { matchId }),
      breadcrumbs: gridSubheaderBreadcrumbSelector(state, matchId),
    };

    if (match) {
      props.slotType = match.slotType;
      props.matchName = match.name;
      props.sportId = match.sportId;
      props.tournamentUnionId = match.tournamentUnionId;
      props.tournamentId = match.tournamentId;
      props.tournamentStageId = match.tournamentStageId;

      // props.matchAdditionalText = match.attributes?.get('match-of-the-day')?.text;
      props.startDate = match.startDate;

      props.firstTeam = matchObjects?.get(1);
      props.secondTeam = matchObjects?.get(2);
    }

    return props;
  };
};

export default connect(mapStateToProps)(MatchTile);
